/**
 * STAND EANCOM Configuration
 * Based on: https://stand.no/en/articles/197-implementation-of-edi-in-the-retail-trade/
 * 
 * This file contains specific validation rules for EANCOM messages
 * according to STAND guidelines for retail trade in Norway.
 */

/**
 * Message type definitions
 */
export const MESSAGE_TYPES = {
  'ORDERS': {
    name: 'Purchase Order',
    mandatorySegments: ['UNB', 'UNH', 'BGM', 'DTM', 'NAD', 'UNT', 'UNZ'],
    conditionalSegments: ['RFF', 'CUX', 'PAT', 'TDT', 'TOD', 'LIN', 'PIA', 'IMD', 'QTY', 'PRI', 'TAX', 'MOA', 'ALC'],
    mandatoryParties: ['BY', 'SU'], // Buyer and Supplier
    mandatoryDates: ['137', '4'], // Document date, Order date
    conditionalDates: ['2', '64', '63', '10'], // Delivery dates
    bilateralAgreements: [
      'Use of order type Forecast',
      'Use of order type Industrial Sales / Representative orders',
      'Specific requested delivery/pick-up date codes',
      'Free text information usage',
      'Quantity delivered in Order Confirmation',
      'Availability date for sold-out products',
      'Free text usage in Order Confirmation',
      'Order Confirmation deadline'
    ]
  },
  'ORDRSP': {
    name: 'Order Response',
    mandatorySegments: ['UNB', 'UNH', 'BGM', 'DTM', 'NAD', 'UNT', 'UNZ'],
    conditionalSegments: ['RFF', 'CUX', 'LIN', 'PIA', 'IMD', 'QTY', 'PRI', 'TAX', 'MOA'],
    mandatoryParties: ['BY', 'SU'], // Buyer and Supplier
    mandatoryDates: ['137'], // Document date
    bilateralAgreements: [
      'Whether Order Receipt shall be sent',
      'Deadline for Order Confirmation',
      'Whether Invoice Receipt shall be sent'
    ]
  },
  'DESADV': {
    name: 'Despatch Advice',
    mandatorySegments: ['UNB', 'UNH', 'BGM', 'DTM', 'NAD', 'UNT', 'UNZ'],
    conditionalSegments: ['RFF', 'CPS', 'PAC', 'PCI', 'LIN', 'PIA', 'IMD', 'QTY', 'GIN'],
    mandatoryParties: ['BY', 'SU', 'DP'], // Buyer, Supplier, Delivery party
    mandatoryDates: ['137', '11'], // Document date, Despatch date
    conditionalDates: ['2', '17', '35'], // Delivery dates
    bilateralAgreements: [
      'Number of load carriers in pallet exchange scheme',
      'Number of pallet places the shipment requires in transport',
      'Promotional Unit in level 3 of Despatch Advice',
      'Deadline for Despatch Advice at EXW'
    ]
  },
  'INVOIC': {
    name: 'Invoice',
    mandatorySegments: ['UNB', 'UNH', 'BGM', 'DTM', 'NAD', 'CUX', 'LIN', 'UNS', 'MOA', 'UNT', 'UNZ'],
    conditionalSegments: ['RFF', 'PAT', 'PYT', 'ALC', 'PIA', 'IMD', 'QTY', 'PRI', 'TAX'],
    mandatoryParties: ['BY', 'SU'], // Buyer and Supplier
    mandatoryDates: ['137'], // Document date
    conditionalDates: ['3', '130', '131', '171', '140'], // Invoice date, Reference dates, payment dates
    bilateralAgreements: [
      'Whether simple or advanced version of Invoice Receipt must be sent'
    ]
  },
  'RECADV': {
    name: 'Receiving Advice',
    mandatorySegments: ['UNB', 'UNH', 'BGM', 'DTM', 'NAD', 'CPS', 'LIN', 'UNT', 'UNZ'],
    conditionalSegments: ['RFF', 'QTY', 'PIA'],
    mandatoryParties: ['BY', 'SU'],
    mandatoryDates: ['137', '50'], // Document date, Goods receipt date
    bilateralAgreements: []
  }
};

/**
 * Distribution types with specific validation rules
 */
export const DISTRIBUTION_TYPES = {
  'DISTRIBUTOR': {
    name: 'Delivery via distributor',
    description: 'Goods are delivered via a distributor',
    indicators: [
      'NAD+DP segment not present',
      'No crossdock handling instructions'
    ]
  },
  'DIRECT': {
    name: 'Delivery directly to retailer',
    description: 'Goods are delivered directly to retailer',
    indicators: [
      'NAD+DP segment present',
      'No crossdock handling instructions'
    ]
  },
  'CROSSDOCK': {
    name: 'Crossdock delivery',
    description: 'Delivery via crossdock terminal',
    indicators: [
      'NAD+DP segment with crossdock terminal GLN',
      'HAN segment with handling instructions',
      'GIN segment with SSCC codes',
      'Ultimate customer identification at line level'
    ],
    additionalRules: [
      'Each unit must be labeled with SSCC',
      'SSCC must be included in Despatch Advice',
      'Handling instructions must be provided',
      'End receiver must be identified'
    ],
    bilateralAgreements: [
      'Whether replacement products can be delivered',
      'Whether products not ordered can be added to delivery',
      'Whether Ultimate Customer can be sent at line level'
    ]
  }
};

/**
 * Required code values by STAND
 */
export const CODE_VALUES = {
  // Date qualifiers
  'DTM_QUALIFIERS': {
    '2': 'Delivery date/time, requested',
    '3': 'Invoice date/time',
    '4': 'Order date/time',
    '7': 'Effective date/time',
    '10': 'Shipment date/time, requested',
    '11': 'Despatch date and/or time',
    '12': 'Terms delivery date/time',
    '13': 'Terms discount due date/time',
    '15': 'Promotion start date/time',
    '16': 'Promotion end date/time',
    '17': 'Delivery date/time, estimated',
    '23E': 'Delivery date/time, earliest',
    '35': 'Delivery date/time, actual',
    '36': 'Expiry date',
    '37': 'Ship not before date/time',
    '38': 'Ship not later than date/time',
    '50': 'Goods receipt date/time',
    '54E': 'Stuffing date/time (GS1 Temporary Code)',
    '61': 'Cancel if not delivered by date/time',
    '63': 'Latest delivery date/time',
    '64': 'Earliest delivery date/time',
    '69': 'Delivery date/time, promised for',
    '71': 'Delivery date/time, requested for (after and including)',
    '72': 'Delivery date/time, requested for (before and including)',
    '76': 'Production/manufacture date',
    '94': 'Manufacture date',
    '101': 'Issue date',
    '137': 'Document/message date/time',
    '140': 'Payment due date',
    '171': 'Reference date/time',
    '189': 'Departure date/time, scheduled',
    '200': 'Pick-up/collection date/time',
    '232': 'Arrival date/time, scheduled',
    '263': 'Invoicing period start date',
    '264': 'Invoicing period end date'
  },
  
  // Party qualifiers
  'NAD_QUALIFIERS': {
    'BY': 'Buyer',
    'SU': 'Supplier',
    'SE': 'Seller',
    'DP': 'Delivery party',
    'IV': 'Invoicee',
    'ST': 'Ship to',
    'CN': 'Consignee',
    'II': 'Invoice issuer',
    'UC': 'Ultimate consignee',
    'CA': 'Carrier',
    'FR': 'Message from',
    'TO': 'Message to',
    'PE': 'Payee',
    'PR': 'Payer',
    'OB': 'Ordered by',
    'SF': 'Ship from',
    'BT': 'Bill to',
    'RE': 'Remit to'
  },
  
  // Reference qualifiers
  'RFF_QUALIFIERS': {
    'ON': 'Order number',
    'CT': 'Contract number',
    'DQ': 'Delivery note number',
    'AAK': 'Despatch advice number',
    'IV': 'Invoice number',
    'VN': 'Order number (supplier)',
    'ADE': "Customer's reference number",
    'ACW': 'Reference number to previous message',
    'AAS': 'Transport document number',
    'AAJ': 'Delivery schedule number',
    'ABO': 'Originator\'s reference',
    'CR': 'Customer reference number',
    'PD': 'Promotion deal number',
    'UC': 'Ultimate customer\'s order number',
    'SI': 'Shipment identification number',
    'BM': 'Bill of lading number',
    'CN': 'Carrier\'s reference number',
    'SRN': 'Shipment reference number',
    'TN': 'Transaction reference number',
    'POR': 'Purchase order response number',
    'ALO': 'Allocation number',
    'PK': 'Packing list number'
  },
  
  // Document types
  'BGM_CODES': {
    '105': 'Purchase order',
    '220': 'Order',
    '231': 'Purchase order change',
    '351': 'Despatch advice',
    '380': 'Commercial invoice',
    '383': 'Debit note',
    '386': 'Prepayment invoice',
    '389': 'Self-billed invoice',
    '481': 'Remittance advice'
  },
  
  // Quantity qualifiers from EANCOM standard
  'QTY_QUALIFIERS': {
    '1': 'Discrete quantity',
    '21': 'Ordered quantity',
    '46': 'Delivered quantity',
    '47': 'Invoiced quantity',
    '52': 'Quantity per pack',
    '59': 'Number of consumer units in the traded unit',
    '61': 'Return quantity',
    '83': 'Backorder quantity',
    '113': 'Quantity in previous package',
    '192': 'Free goods quantity'
  },
  
  // Standard approved units of measure
  'UNITS_OF_MEASURE': [
    'PCE', // Piece
    'KGM', // Kilogram
    'LTR', // Liter
    'MTR', // Meter
    'CMT', // Centimeter
    'MMT', // Millimeter
    'GRM', // Gram
    'MLT', // Milliliter
    'PA',  // Package
    'CS',  // Case
    'CT',  // Carton
    'PL',  // Pallet
    'EA'   // Each
  ],
  
  // Norwegian VAT rates as of 2023
  'NORWEGIAN_VAT_RATES': [
    '25', // Standard rate
    '15', // Food and beverages
    '12', // Passenger transport, accommodation
    '0'   // Zero-rated (exports, etc.)
  ],
  
  // Packaging types commonly used in Norwegian retail
  'PACKAGING_TYPES': {
    '201': 'Pallet ISO 1 - 1/1 EURO Pallet',
    '202': 'Pallet ISO 2 - 1/2 EURO Pallet',
    '203': 'Pallet ISO 0 - 1/4 EURO Pallet',
    '09': 'Returnable pallet',
    'CT': 'Carton',
    'CS': 'Case',
    'BO': 'Bottle',
    'BX': 'Box'
  }
};

/**
 * Norwegian retail specific message requirements based on STAND
 */
export const NORWEGIAN_RETAIL_REQUIREMENTS = {
  // Time limits for message exchanges
  'TIME_LIMITS': {
    'ORDRSP': {
      'standard': '2 hours from receipt of order',
      'crossdock': '30 minutes from receipt of order'
    },
    'DESADV': {
      'standard': 'Before physical delivery',
      'crossdock': 'At least 30 minutes before arrival',
      'direct': 'At latest upon loading of goods'
    },
    'INVOIC': {
      'standard': 'Within 24 hours of dispatch',
      'electronic': 'Same day as DESADV'
    }
  },
  
  // Standard delivery window timeframes
  'DELIVERY_WINDOWS': {
    'DEFAULT': '+/- 30 minutes from agreed delivery time',
    'CROSSDOCK': '+/- 15 minutes from agreed delivery time',
    'DIRECT_STORE': 'During store opening hours'
  },
  
  // Norwegian regulations for invoice requirements
  'INVOICE_REQUIREMENTS': {
    'MANDATORY_FIELDS': [
      'Invoice number',
      'Invoice date',
      'Seller organization number',
      'Buyer organization number',
      'Currency code',
      'Total amount excluding VAT',
      'Total VAT amount',
      'Total amount including VAT'
    ],
    'CONDITIONAL_FIELDS': [
      'Delivery date',
      'Payment terms',
      'Bank account number',
      'KID number (Norwegian payment reference)'
    ]
  },
  
  // SSCC label requirements for logistic units
  'SSCC_REQUIREMENTS': {
    'MANDATORY': [
      'SSCC (Serial Shipping Container Code)',
      'GLN of sender',
      'Content description',
      'Human readable number'
    ],
    'CONDITIONAL': [
      'Batch/lot number',
      'Best before date',
      'Weight information'
    ],
    'LABEL_FORMAT': 'GS1-128',
    'PLACEMENT': 'Right side of the short end of the pallet'
  }
};

/**
 * Bilateral agreements that should be established between trading partners
 */
export const BILATERAL_AGREEMENTS = {
  'GENERAL': [
    'Cutoff times for order processing',
    'Default delivery times',
    'Returns handling procedures',
    'Message acknowledgment requirements',
    'Contact details for EDI support'
  ],
  'ORDERS': [
    'Use of forecast orders',
    'Minimum order quantities',
    'Order cancellation timeframes',
    'Handling of substitute products'
  ],
  'DESADV': [
    'Packaging hierarchy levels required',
    'SSCC labeling requirements',
    'Deadline for sending Despatch Advice before delivery',
    'Specific transport information requirements'
  ],
  'INVOIC': [
    'Use of self-billing',
    'Invoice consolidation rules',
    'Credit note procedures',
    'VAT handling for different product categories'
  ],
  'CROSSDOCK': [
    'Terminal-specific requirements',
    'Handling of mixed pallets',
    'Marking of retail-ready packaging',
    'Specific crossdock terminal GLNs',
    'Required arrival notification (time window)'
  ]
};

/**
 * STAND compliant message relationship flows
 */
export const MESSAGE_FLOWS = {
  'STANDARD': [
    { from: 'ORDERS', to: 'ORDRSP', mandatory: true, timeframe: '2 hours' },
    { from: 'ORDRSP', to: 'DESADV', mandatory: true, timeframe: 'At delivery' },
    { from: 'DESADV', to: 'RECADV', mandatory: false, timeframe: 'After goods receipt' },
    { from: 'DESADV', to: 'INVOIC', mandatory: true, timeframe: '24 hours' }
  ],
  'CROSSDOCK': [
    { from: 'ORDERS', to: 'ORDRSP', mandatory: true, timeframe: '30 minutes' },
    { from: 'ORDRSP', to: 'DESADV', mandatory: true, timeframe: 'Before arrival' },
    { from: 'DESADV', to: 'RECADV', mandatory: true, timeframe: 'Upon arrival' },
    { from: 'DESADV', to: 'INVOIC', mandatory: true, timeframe: 'Same day' }
  ]
};

/**
 * Detect distribution type based on message content
 * @param {object} parsedData - Parsed EDI data
 * @returns {string} Distribution type key or 'UNKNOWN'
 */
export const detectDistributionType = (parsedData) => {
  const { segments } = parsedData;
  
  // Check for crossdock indicators
  const hasDeliveryParty = segments.some(seg => 
    seg.id === 'NAD' && seg.elements && seg.elements.length > 0 && seg.elements[0] === 'DP'
  );
  
  const hasHandlingInstructions = segments.some(seg => seg.id === 'HAN');
  
  const hasSSCC = segments.some(seg => 
    seg.id === 'GIN' && seg.elements && seg.elements.length > 0 && 
    (seg.elements[0] === 'BJ' || (Array.isArray(seg.elements[0]) && seg.elements[0][0] === 'BJ'))
  );
  
  // Determine type based on indicators
  if (hasSSCC && hasHandlingInstructions) {
    return 'CROSSDOCK';
  } else if (hasDeliveryParty) {
    return 'DIRECT';
  } else {
    return 'DISTRIBUTOR';
  }
};

/**
 * Get required bilateral agreements based on message and distribution type
 * @param {string} messageType - Message type code
 * @param {string} distributionType - Distribution type key
 * @returns {Array} List of bilateral agreements that may be required
 */
export const getBilateralAgreements = (messageType, distributionType) => {
  const messageAgreements = MESSAGE_TYPES[messageType]?.bilateralAgreements || [];
  const distributionAgreements = DISTRIBUTION_TYPES[distributionType]?.bilateralAgreements || [];
  const generalAgreements = BILATERAL_AGREEMENTS['GENERAL'] || [];
  const specificMessageAgreements = BILATERAL_AGREEMENTS[messageType] || [];
  
  let allAgreements = [
    ...generalAgreements,
    ...messageAgreements,
    ...distributionAgreements,
    ...specificMessageAgreements
  ];
  
  // Add crossdock specific agreements if applicable
  if (distributionType === 'CROSSDOCK') {
    allAgreements = [...allAgreements, ...BILATERAL_AGREEMENTS['CROSSDOCK']];
  }
  
  return allAgreements;
};

/**
 * Get the time limits for sending messages based on message type and distribution
 * @param {string} messageType - Message type code
 * @param {string} distributionType - Distribution type key
 * @returns {string} Time limit description
 */
export const getMessageTimeLimit = (messageType, distributionType) => {
  const timeLimits = NORWEGIAN_RETAIL_REQUIREMENTS.TIME_LIMITS;
  
  if (messageType in timeLimits) {
    // Check if there's a distribution-specific time limit
    if (distributionType.toLowerCase() in timeLimits[messageType]) {
      return timeLimits[messageType][distributionType.toLowerCase()];
    }
    // Otherwise return the standard time limit
    if ('standard' in timeLimits[messageType]) {
      return timeLimits[messageType].standard;
    }
  }
  
  return 'No specific time limit defined';
};

/**
 * Get appropriate VAT rates for Norwegian trade
 * @returns {Array} Valid Norwegian VAT rates
 */
export const getNorwegianVatRates = () => {
  return CODE_VALUES.NORWEGIAN_VAT_RATES;
};

/**
 * Get standard approved units of measure
 * @returns {Array} Approved units of measure
 */
export const getApprovedUnitsOfMeasure = () => {
  return CODE_VALUES.UNITS_OF_MEASURE;
};

export default {
  MESSAGE_TYPES,
  DISTRIBUTION_TYPES,
  CODE_VALUES,
  NORWEGIAN_RETAIL_REQUIREMENTS,
  BILATERAL_AGREEMENTS,
  MESSAGE_FLOWS,
  detectDistributionType,
  getBilateralAgreements,
  getMessageTimeLimit,
  getNorwegianVatRates,
  getApprovedUnitsOfMeasure
}; 